import React from 'react';
import { ArrowLeft, Users } from 'lucide-react';
import Button from '../Button';
import StepContainer from '../StepContainer';
import { cn } from '../../lib/utils';

interface VitaminSegment {
  name: string;
  description: string;
  profiles: number;
}

interface VitaminSegmentSelectorProps {
  segments: VitaminSegment[];
  selectedSegments: string[];
  onSegmentToggle: (segmentName: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  onBack: () => void;
  onNext: () => void;
  isSubmitting?: boolean;
  submitButtonText?: string;
  isDemoUser?: boolean;
}

const VitaminSegmentSelector: React.FC<VitaminSegmentSelectorProps> = ({
  segments,
  selectedSegments,
  onSegmentToggle,
  onSelectAll,
  onClearAll,
  onBack,
  onNext,
  isSubmitting = false,
  submitButtonText = 'Run Concept Test',
  isDemoUser = false
}) => {
  // Demo user override for display
  const DEMO_N_AGENTS = 1000;

  // Calculate totals
  const totalProfiles = segments.reduce((sum, s) => sum + s.profiles, 0);
  const selectedProfiles = segments
    .filter(s => selectedSegments.includes(s.name))
    .reduce((sum, s) => sum + s.profiles, 0);

  // Display values (demo users see n=1000)
  const displayTotalProfiles = isDemoUser ? DEMO_N_AGENTS : totalProfiles;
  const displaySelectedProfiles = isDemoUser ? DEMO_N_AGENTS : selectedProfiles;

  const allSelected = selectedSegments.length === segments.length;

  return (
    <StepContainer
      title="Select Target Segments"
      subtitle="Choose which consumer segments to include in this test"
      className="animate-fadeIn"
    >
      {/* Header with Select All button */}
      <div className="mb-6 flex justify-between items-center">
        <div className="text-sm text-gray-600">
          Select specific segments or leave empty to run with all {displayTotalProfiles} agents
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={allSelected ? onClearAll : onSelectAll}
          className="bg-blue-400 text-white hover:bg-blue-500"
        >
          {allSelected ? 'Clear All' : 'Select All Segments'}
        </Button>
      </div>

      {/* Segment Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {segments.map((segment) => {
          const isSelected = selectedSegments.includes(segment.name);
          const percentOfTotal = ((segment.profiles / totalProfiles) * 100).toFixed(1);
          // Scale segment profiles to 1000 for demo users
          const displaySegmentProfiles = isDemoUser
            ? Math.round((segment.profiles / totalProfiles) * DEMO_N_AGENTS)
            : segment.profiles;

          return (
            <div key={segment.name} className="relative">
              {/* Checkbox indicator in top-right corner */}
              <div className="absolute top-3 right-3 z-10">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onSegmentToggle(segment.name)}
                    className="sr-only"
                  />
                  <div className={cn(
                    "w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200",
                    isSelected
                      ? "bg-[#07E5D1] border-[#07E5D1]"
                      : "bg-white border-gray-300 hover:border-gray-400"
                  )}>
                    {isSelected && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </label>
              </div>

              {/* Card */}
              <div
                className={cn(
                  "border rounded-lg p-4 transition-all duration-200 cursor-pointer h-full",
                  isSelected
                    ? 'bg-blue-50 border-blue-300 shadow-md'
                    : 'border-gray-200 hover:border-blue-400 hover:shadow-sm'
                )}
                onClick={() => onSegmentToggle(segment.name)}
              >
                <div className="w-full pr-6">
                  {/* Segment Name */}
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {segment.name}
                  </h3>

                  {/* Profile Count */}
                  <div className="flex items-center text-sm text-gray-600 mb-3">
                    <Users className="w-4 h-4 mr-1.5 text-gray-400" />
                    <span className={isSelected ? "font-medium text-blue-600" : ""}>
                      {displaySegmentProfiles} profiles ({percentOfTotal}%)
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {segment.description}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation */}
      <div className="pt-6 border-t border-gray-200">
        {/* Error message when no segments selected */}
        {selectedSegments.length === 0 && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start">
            <svg className="w-5 h-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <p className="text-amber-800 text-sm font-medium">Please select at least one segment to continue</p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            <div>
              {selectedSegments.length === 0
                ? 'No segments selected'
                : `${selectedSegments.length} of ${segments.length} segments selected`
              }
            </div>
            {selectedSegments.length > 0 && (
              <div className="text-xs mt-1">
                Total: {displaySelectedProfiles} profiles
              </div>
            )}
          </div>

          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={onBack}
              icon={<ArrowLeft className="h-4 w-4" />}
            >
              Back
            </Button>
            <Button
              variant="primary"
              onClick={onNext}
              disabled={isSubmitting || selectedSegments.length === 0}
              withArrow
            >
              {isSubmitting ? 'Processing...' : submitButtonText}
            </Button>
          </div>
        </div>
      </div>
    </StepContainer>
  );
};

export default VitaminSegmentSelector;
